// src/utils/axiosInterceptor.ts
import { api } from '../services/api';
import { performanceStore } from './performanceMetricsStore';

const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

// Pages reachable while signed out - a 401 while sitting on any of these
// must not hard-redirect (that would kick a visitor off the very page
// they need to recover access, e.g. mid password-reset).
const PUBLIC_PATHS = ['/login', '/forgot-password', '/reset-password'];

const clearAuthAndRedirect = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('authUser');
  if (!PUBLIC_PATHS.includes(window.location.pathname)) {
    // This is a hard browser navigation (not a React Router transition), so
    // React Router's location.state can't survive it. Instead, carry the
    // "return to" path in the URL itself via a ?from= query param, which
    // LoginPage reads (and validates) after a successful login.
    const from = encodeURIComponent(
      window.location.pathname + window.location.search
    );
    window.location.href = `/login?from=${from}`;
  }
};

const logClientError = async (errorData: {
  message: string;
  stackTrace?: string;
  url: string;
  statusCode?: number;
  httpMethod?: string;
  context?: string;
  userAgent: string;
}) => {
  try {
    const authUser = localStorage.getItem('authUser');
    const userId = authUser ? JSON.parse(authUser).id : null;

    const backendUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:5001';

    await fetch(`${backendUrl}/api/errorlogs/frontend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...errorData,
        userId,
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {});
  } catch {
    // fail silently
  }
};

export const setupAxiosInterceptors = () => {
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('authToken');

      if (token) {
        if (isTokenExpired(token)) {
          clearAuthAndRedirect();
          return Promise.reject(new Error('Token expired'));
        }
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  api.interceptors.response.use(
    (response) => {
      if (response.config.skipPerfMetric) return response;

      const endpoint = response.config.url || 'unknown';
      const responseTime = response.headers['x-response-time-ms'];
      const statusCode = response.status;

      performanceStore.recordMetric(endpoint, responseTime, statusCode);

      return response;
    },
    (error) => {
      const statusCode = error.response?.status;
      const url = error.config?.url || window.location.pathname;
      const httpMethod = error.config?.method?.toUpperCase() || 'UNKNOWN';
      const errorMessage =
        error.response?.data?.message || error.message || 'Unknown API error';

      // 401 from server means token was rejected (e.g. revoked or tampered) — clear and redirect
      if (statusCode === 401) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      logClientError({
        message: `API Error: ${errorMessage}`,
        url,
        statusCode,
        httpMethod,
        context: `Endpoint: ${url}`,
        userAgent: navigator.userAgent,
      });

      // OPTIMISTIC LOCKING: Mark 409 Conflict errors for special handling
      if (statusCode === 409) {
        const conflictError = error as any;
        conflictError.isConflict = true;
        conflictError.conflictData = error.response?.data;
        return Promise.reject(conflictError);
      }

      return Promise.reject(error);
    }
  );
};
